<script lang="ts" module>
  /* THE MAP LIFECYCLE, once, for every host that wants a chart.
   *
   * ═══ WHAT THIS OWNS, AND WHY IT IS EXACTLY THIS MUCH ═══
   *
   * Everything about running a MapLibre map that is the same whether there is a
   * boat behind it or not: the lazy import, registering the tile protocol
   * against THE SAME module the map is built from, the sweep that drives every
   * live layer once a second, publishing the viewport to the layer manager, the
   * teardown, and the two sentences a user sees while it starts or when it does
   * not.
   *
   * WHICH LAYERS is the one thing it does not decide. That is the whole point:
   * a hosted planning chart, the boat's dash and a page embedding
   * `<gennetay-chart>` want three different sets, and every one of them wants
   * the same lifecycle. `build` is that seam.
   *
   * ═══ ONE CHART PER PAGE, AND IT SAYS SO ═══
   *
   * `layers` in chart-core is a module-level singleton — deliberately, because
   * the panel, the legend and the sheet all reach for it without being handed
   * it, and a page has never needed two charts. A second instance would attach
   * over the first's map and quietly take its layers with it, so a second
   * instance REFUSES and says why. That is worth stating rather than
   * discovering: an embedding host can put two custom elements on a page by
   * accident, and "the first chart went blank" is a hard fault to read
   * backwards.
   */
  import type { Map as MlMap } from 'maplibre-gl';

  import type { Bbox } from '@gennetay/chart-core/lib/geo';
  import type { ChartEnv } from '@gennetay/chart-core/map/env';
  import type { LayerModule } from '@gennetay/chart-core/map/types';

  /* Re-exported so a host importing the component does not also have to know
   * which package the contract lives in. */
  export type { ChartEnv };

  export interface ChartProps {
    /** The layer modules this host wants, in REGISTRATION ORDER — which is
     *  load-bearing: within a band, layers stack in the order they are
     *  registered. Async because a host lazily imports its packs. */
    build: (env: ChartEnv) => LayerModule[] | Promise<LayerModule[]>;
    /** Extra work on the 1 Hz sweep — the per-view feeds a host drives itself.
     *  Called from HERE rather than from a map event for the reason recorded
     *  against the sweep below. */
    tick?: (env: ChartEnv) => void;
    center?: [number, number];
    zoom?: number;
    night?: boolean;
    /** Where the style's own assets live — glyphs, sprite. A host under a
     *  sub-path passes its base URL; the default suits a site at the root. */
    assetBase?: string;
    /** Put `__map`, `__layers` and `__sources` on globalThis. The proofs drive
     *  the chart through these. A PROP rather than `import.meta.env.DEV`,
     *  because a package must not depend on the bundler its host happens to
     *  use — and the prebuilt custom element has no bundler env at all. */
    expose?: boolean;
    /** Whether the animated layers should run — see `ChartEnv.animate`. The
     *  default is the only thing a host with no other state can know. */
    animate?: () => boolean;
    /**
     * A tap on the chart, in geographic coordinates and in screen pixels.
     *
     * ═══ WHY THE COMPONENT DOES NOT DECIDE WHAT A TAP MEANS ═══
     *
     * A ruler wants the point. A pick wants to know what is within fourteen
     * pixels of it. An autoroute wants a leg end. Those are three different
     * questions about the same gesture, and only the host knows which one is
     * being asked — so this reports the gesture and stops.
     *
     * `point` is in pixels because a hit test has to be: the finger is a fixed
     * size and the chart is not, and a radius in metres is a third of a pixel at
     * zoom 9 and half the screen at zoom 18.
     */
    ontap?: (at: { lat: number; lon: number }, point: { x: number; y: number }) => void;
    /**
     * A DRAG on the chart, when a host claims it.
     *
     * ═══ THE HOST DECIDES WHETHER THIS IS A PAN ═══
     *
     * `ondragstart` is asked first and returns true to CLAIM the gesture —
     * "there is a route vertex under that pixel". Only then are `ondragmove`
     * and `ondragend` called, and only then is the map's own panning
     * suppressed. Unclaimed, MapLibre drags the chart exactly as before, which
     * is what a chart must keep doing by default.
     *
     * Pointer events rather than MapLibre's `mousedown`, because these have to
     * work under a finger: a touch produces no mouse events until it ends, and
     * a vertex you can only move with a mouse is a vertex the boat cannot move
     * at all.
     */
    ondragstart?: (at: { lat: number; lon: number }, point: { x: number; y: number }) => boolean;
    ondragmove?: (at: { lat: number; lon: number }, point: { x: number; y: number }) => void;
    ondragend?: (at: { lat: number; lon: number }, point: { x: number; y: number }) => void;
    /**
     * Perspective view — terrain, sky and a tilt.
     *
     * ═══ WHY THE COMPONENT OWNS THIS AND NOT THE HOST ═══
     *
     * It is the map's own CAMERA, not a layer: the pitch cap, whether a drag
     * may rotate, the terrain source and the fog. A host cannot set it without
     * reaching into the map, and the terrain source speaks a custom protocol
     * that only this component can register against the right MapLibre module
     * instance — the same trap the tile protocol carries a warning about.
     *
     * ═══ NO THREE.JS ═══
     *
     * The boat's 3D also draws a hull and AIS targets as models, which is what
     * pulls in 182 kB of three.js. That is `scene3d`, a separate layer in the
     * boat pack. The perspective view itself — terrain, sky, tilt — is native
     * MapLibre, so a host with no vessel to draw gets 3D for nothing.
     */
    threeD?: boolean;
    /** How hard the seabed is exaggerated. On top of the 2.5× baked into
     *  Seascape's own gain, which is why the boat shows the number rather than
     *  hiding it behind a slider. */
    exaggeration?: number;
    /** Host loading grace expired before MapLibre reached its first idle. */
    loadingTimedOut?: boolean;
    /** Chart-tile requests observed during that grace. Zero diagnoses registration, not slowness. */
    loadingRequested?: number;
    onloadingretry?: () => void;
    onready?: (map: MlMap) => void;
  }

  /** Guards the singleton. Module-level, so it survives a component's own
   *  teardown/remount and only a genuine second instance trips it. */
  let mounted = 0;
</script>

<script lang="ts">
  import { untrack } from 'svelte';
  import { layers } from '@gennetay/chart-core/map/layers.svelte';
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
  import { baseStyle } from '@gennetay/chart-core/map/style';
  import { isUpdatable } from '@gennetay/chart-core/map/types';
  import { sources } from '@gennetay/chart-core/sources';
  import {
    hasFailedVisibleChartTiles,
    hasInklessVisibleChartTiles,
    hasUnsettledVisibleChartTiles,
    slowTilesNoticeVisible
  } from '@gennetay/chart-core/map/slow-tiles';
  import ChartLoadingNotice from './ChartLoadingNotice.svelte';
  import SlowTilesNotice from './SlowTilesNotice.svelte';

  const {
    build,
    tick,
    center = [-123.9, 49.3],
    zoom = 9,
    night = false,
    assetBase = '/',
    expose = false,
    animate = () => !document.hidden,
    ontap,
    ondragstart,
    ondragmove,
    ondragend,
    threeD = false,
    exaggeration = 1.4,
    loadingTimedOut = false,
    loadingRequested = 0,
    onloadingretry,
    onready
  }: ChartProps = $props();

  /* ═══ THE SWEEP IS NOT THE ONLY WAY TO REDRAW, AND IT CANNOT BE ═══
   *
   * A 1 Hz loop is right for LIVE data — a boat, a barb, a tide bar. It is
   * completely wrong for anything a finger is doing: a vertex dragged at
   * pointer rate redrew twice a second, so the point stuck and jumped a whole
   * second behind the finger, and a scrubber dragged across six hours moved the
   * tide symbols in six visible steps.
   *
   * The boat learned this and its comment is worth quoting: "costs nothing" and
   * "draws nothing" are the same measurement, and only one of them is good
   * news. Its answer is targeted effects that redraw ONE layer on the state
   * that changed, rather than running every module at pointer rate — which
   * would be ~60 passes a second over twenty layers.
   *
   * `refresh` is that seam. A host runs its own effect on whatever it changed
   * and names the layers it touched; everything else keeps its 1 Hz. */
  let ctx: { map: MlMap; beforeIdFor: () => undefined } | null = null;

  /* ═══ A LAYER THAT IS NOT DRAWN DOES NOT NEED ITS DATA REBUILT ═══
   *
   * Every `update()` rebuilds a GeoJSON FeatureCollection and hands it to
   * `setData`, which re-tiles it. MEASURED with everything switched on: 5.18 ms
   * a sweep, of which `tide-stations` is 3.6 ms building 158 features and
   * `currents` 1.31 ms building 29 — and NOT ONE of the modules checks whether
   * it is visible first. So the default chart, where most rows are off, was
   * paying nearly all of that to update sources nothing renders.
   *
   * It matters far more than 5 ms a second suggests, because `refresh` runs the
   * same list at POINTER RATE during a scrub. Seventeen repaints in a fast drag
   * is seventeen times whatever this costs.
   *
   * Here rather than in each module: it is the same rule for every one of them,
   * and a rule twenty layers have to remember is a rule some of them will not. */
  const drawn = (id: string): boolean => {
    try {
      return layers.isVisible(id);
    } catch {
      /* A module still registering has no state yet. Draw it — a wasted frame
       * is cheaper than a layer that never starts. */
      return true;
    }
  };

  export function refresh(ids?: readonly string[]): void {
    const c = ctx;
    if (!c) return;
    /* RE-SAMPLED HERE TOO. The cursor is what a scrub moves, and a layer
     * redrawn against the sweep's second-old snapshot would draw the instant
     * the finger has already left. Sampled ONCE per refresh, so every layer in
     * this pass still agrees about when it is. */
    now = viewTime.now;
    const mods = ids ? ids.flatMap((i) => layers.get(i) ?? []) : layers.list();
    for (const mod of mods) {
      if (!isUpdatable(mod) || !drawn(mod.id)) continue;
      try {
        mod.update(c);
      } catch (e) {
        console.warn(`[chart] layer "${mod.id}" failed to refresh:`, e);
      }
    }
  }

  let container: HTMLDivElement;
  /* Held reactively so the 3D effect below can act on it. The one inside the
   * mount effect is deliberately local — it is torn down with the map. */
  let mapRef = $state<MlMap | null>(null);
  let ready = $state(false);
  let error = $state<string | null>(null);
  let canadianGap = $state(false);
  let canadianGapDismissed = $state(false);
  let chartOutages = $state<{ id: string; name: string }[]>([]);
  let chartOutagesDismissed = $state<Record<string, boolean>>({});
  let noOfficialCharts = $state(false);
  let noOfficialChartsDismissed = $state(false);
  let slowTilesFailed = $state(false);
  let slowTilesUnsettled = $state(false);
  let slowTilesInkless = $state(false);
  let slowTilesGraceElapsed = $state(false);
  let slowTilesDismissed = $state(false);
  let refreshChartNotices: (() => void) | null = null;

  const affectedLayerNames = () => chartOutages.map((outage) => outage.name).join(' and ');
  const retryLoadingCharts = async () => {
    const m = mapRef;
    if (!m) return;
    slowTilesFailed = false;
    slowTilesUnsettled = false;
    slowTilesInkless = false;
    slowTilesGraceElapsed = false;
    const diagnosed = chartOutages.map((outage) => outage.id);
    const affected = diagnosed.length > 0
      ? diagnosed
      : ['chs-enc', 'noaa-enc'].filter((id) => layers.state[id]?.visible === true);
    const tileProtocol = await import('@gennetay/chart-core/map/tile-protocol');
    await tileProtocol.retryFailedChartTiles(m, affected, (ids) => layers.retry(ids));
    onloadingretry?.();
  };

  /* ═══ THE CHART'S CLOCK IS THE TIME CURSOR, ALWAYS ═══
   *
   * Not `Date.now()`. `viewTime.now` IS the wall clock until something parks
   * it, so a host with no scrubber is unaffected — and a host that adds one
   * gets every prediction layer moving together for free, because they all read
   * this one number through `ChartEnv.now`.
   *
   * Reading it here rather than in each layer is what makes them agree: sampled
   * ONCE per sweep, so a tide bar and the stream arrow beside it cannot land on
   * instants a few milliseconds apart.
   *
   * NOT `$state`. It is read from layer closures the sweep calls, never from
   * markup, so reactivity would buy nothing and would invalidate the component
   * every second. */
  let now = viewTime.now;

  $effect(() => {
    if (mounted > 0) {
      error =
        'a second chart cannot mount on this page — the layer manager is a singleton, ' +
        'and this one would take the first chart’s layers with it';
      return;
    }
    mounted++;

    let cancelled = false;
    let stop: (() => void) | null = null;
    let map: MlMap | null = null;

    void (async () => {
      try {
        const ml = await import('maplibre-gl');
        if (cancelled) return;

        /* Registered with THE SAME MapLibre MODULE the map is built from. A
         * second `import('maplibre-gl')` under a bundler is a different module
         * object with its own empty protocol registry, and the map would never
         * see the handler — no error, no warning, just tiles that never arrive.
         * The dash learned this the expensive way; tile-protocol.ts carries the
         * long version. */
        const [tileProtocol, { registerTerrainProtocol }] = await Promise.all([
          import('@gennetay/chart-core/map/tile-protocol'),
          import('@gennetay/chart-core/map/terrain-protocol')
        ]);
        tileProtocol.registerTileProtocol(
          ml as unknown as Parameters<typeof tileProtocol.registerTileProtocol>[0]
        );
        /* Registered whether or not 3D is ever switched on: it is a handler, it
         * costs nothing idle, and it CANNOT be registered later by a host —
         * only this component holds the MapLibre module the map was built
         * from. */
        registerTerrainProtocol(ml as unknown as Parameters<typeof registerTerrainProtocol>[0]);
        if (cancelled) return;

        const m = new ml.Map({
          container,
          style: baseStyle(night, assetBase),
          center,
          zoom,
          attributionControl: { compact: true },
          /* Rotation is programmatic until 3D turns it on — a 2D chart that can
           * be twisted off north by accident is a chart you have to straighten
           * before you can read it. */
          pitchWithRotate: false,
          dragRotate: false,
          maxPitch: 0
        });
        map = m;
        mapRef = m;

        if (expose) {
          const g = globalThis as Record<string, unknown>;
          g.__map = m;
          g.__layers = layers;
          /* Which backends this build was pointed at. The proofs need it: the
           * chart's tile URL is a WMS template or a `gdtile://` depending on
           * whether a layer cache is configured, and both are correct. */
          g.__sources = sources();
        }

        /* ═══ MAPLIBRE'S OWN CLICK, DELIBERATELY ═══
         *
         * Not a hand-rolled pointerdown/pointerup pair. The boat's ChartPage
         * does roll its own, and its comments say why — a real touch does not
         * reliably produce a click THERE, because its long-press menu and
         * vertex-dragging are already handling the same pointers and the two
         * interfere. None of that exists here, and MapLibre's `click` is
         * synthesised correctly for touch on its own.
         *
         * It also fires only when the pointer did not drag, which is the whole
         * of what a tap detector is for: without it, panning the chart scatters
         * ruler points across the ocean. */
        m.on('click', (e) => {
          ontap?.({ lat: e.lngLat.lat, lon: e.lngLat.lng }, { x: e.point.x, y: e.point.y });
        });

        /* ═══ THE DRAG SEAM ═══
         *
         * Raw pointer events on the canvas, ahead of MapLibre: a touch produces
         * no mouse events until it ends, so a vertex only draggable with a
         * mouse is one the boat cannot move at all.
         *
         * The map's pan is disabled only while a drag is CLAIMED and re-enabled
         * on every exit path — including pointercancel and pointerleave, which
         * is where a chart gets permanently stuck if they are forgotten. */
        const canvas = m.getCanvas();
        let dragging = false;
        const ll = (e: PointerEvent) => {
          const r = canvas.getBoundingClientRect();
          const pt = { x: e.clientX - r.left, y: e.clientY - r.top };
          const g = m.unproject([pt.x, pt.y]);
          return { at: { lat: g.lat, lon: g.lng }, pt };
        };
        canvas.addEventListener('pointerdown', (e) => {
          if (!ondragstart) return;
          const { at, pt } = ll(e);
          if (!ondragstart(at, pt)) return;
          dragging = true;
          m.dragPan.disable();
          canvas.setPointerCapture(e.pointerId);
        });
        canvas.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const { at, pt } = ll(e);
          ondragmove?.(at, pt);
        });
        const endDrag = (e: PointerEvent) => {
          if (!dragging) return;
          dragging = false;
          m.dragPan.enable();
          const { at, pt } = ll(e);
          ondragend?.(at, pt);
        };
        canvas.addEventListener('pointerup', endDrag);
        canvas.addEventListener('pointercancel', endDrag);
        canvas.addEventListener('pointerleave', endDrag);

        /* The viewport, READ on the sweep rather than pushed from `moveend`.
         * Calling into the layer manager from a map event runs it inside this
         * effect, and the dash proved where that ends: Svelte throws
         * effect_update_depth_exceeded and the sweep dies silently, taking every
         * live layer's update with it. */
        const bounds = (): Bbox => {
          const b = m.getBounds();
          return { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() };
        };
        const env: ChartEnv = {
          map: m,
          bounds,
          now: () => now,
          zoom: () => m.getZoom(),
          animate
        };

        /* ═══ SUBSCRIBE FIRST, AWAIT SECOND ═══
         *
         * `build` is asynchronous — a host lazily imports its packs — and the
         * map is already loading while that happens. Registering `on('load')`
         * after awaiting it is a race the map wins on a warm cache: MapLibre
         * does NOT call a handler added after the event has fired, so the
         * listener is attached to an event that is already over and NOTHING
         * ever registers. No error, no warning; the chart just sits on "loading
         * the chart…" forever with a perfectly good map underneath it.
         *
         * Measured: that is exactly what happened the first time this component
         * ran, and it looked like a broken import. So the subscription is taken
         * out synchronously and awaited afterwards, with `loaded()` covering the
         * case where it has already happened. */
        const loaded =
          m.loaded()
            ? Promise.resolve()
            : new Promise<void>((resolve) => m.once('load', () => resolve()));

        const modules = await build(env);
        await loaded;
        if (cancelled) return;

        layers.attach(m);
        layers.setView(bounds());
        layers.setNight(night);
        layers.register(...modules);
        ready = true;
        onready?.(m);

        /* The Canadian service returns a drawn crosshatch, rather than an
         * error, outside much of its real footprint. The proxy recognises
         * repeated empty squares and answers 404; the tile protocol records
         * those outcomes. Compare them with the visible grid so loading or one
         * coastal edge square cannot trigger the message. */
        const chartTileSnapshot = () => {
          const b = bounds();
          const clampedZoom = (id: 'chs-enc' | 'noaa-enc') =>
            Math.min(m.getZoom(), m.getSource(id)?.maxzoom ?? 19);
          const chs = tileProtocol.visibleChartTileOutcomes('chs-enc', b, clampedZoom('chs-enc'));
          const noaa = tileProtocol.visibleChartTileOutcomes('noaa-enc', b, clampedZoom('noaa-enc'));
          const chsOn = layers.state['chs-enc']?.visible === true;
          const noaaOn = layers.state['noaa-enc']?.visible === true;
          return {
            bounds: b,
            outcomes: { 'chs-enc': chs, 'noaa-enc': noaa },
            visible: { 'chs-enc': chsOn, 'noaa-enc': noaaOn }
          };
        };
        const chartCoverage = () => {
          const { bounds: b, outcomes, visible } = chartTileSnapshot();
          const chs = outcomes['chs-enc'];
          const noaa = outcomes['noaa-enc'];
          const chsOn = visible['chs-enc'];
          const noaaOn = visible['noaa-enc'];
          const gap = chsOn && noaaOn && chs.total > 0 && chs.noCoverage > chs.total / 2 && noaa.drawn > 0;
          canadianGap = gap;
          if (!gap) canadianGapDismissed = false;

          const coverage = (id: 'chs-enc' | 'noaa-enc') => {
            const declared = layers.get(id)?.coverage;
            return typeof declared === 'function' ? declared() ?? undefined : declared;
          };
          const center = { lon: (b.west + b.east) / 2, lat: (b.south + b.north) / 2 };
          const chsCoverage = coverage('chs-enc');
          const noaaCoverage = coverage('noaa-enc');
          layers.setChartUncovered('chs-enc', tileProtocol.isChartLayerUncovered({
            center,
            outcomes: chs,
            coverage: chsCoverage
          }));
          layers.setChartUncovered('noaa-enc', tileProtocol.isChartLayerUncovered({
            center,
            outcomes: noaa,
            coverage: noaaCoverage
          }));
          noOfficialCharts = tileProtocol.isNoOfficialChartCoverage({
            center,
            chs: { outcomes: chs, coverage: chsCoverage },
            noaa: { outcomes: noaa, coverage: noaaCoverage }
          });
          if (!noOfficialCharts) noOfficialChartsDismissed = false;

          chartOutages = (['chs-enc', 'noaa-enc'] as const).flatMap((id) => {
            const mod = layers.get(id);
            const visible = layers.state[id]?.visible === true;
            return mod && tileProtocol.isChartLayerOutage(visible, outcomes[id])
              ? [{ id, name: mod.title }]
              : [];
          });
          for (const id of ['chs-enc', 'noaa-enc']) {
            if (!chartOutages.some((outage) => outage.id === id) && chartOutagesDismissed[id]) {
              const { [id]: _cleared, ...stillDismissed } = chartOutagesDismissed;
              chartOutagesDismissed = stillDismissed;
            }
          }
          return { bounds: b, outcomes, visible };
        };
        const recomputeSlowTiles = (
          snapshot: ReturnType<typeof chartTileSnapshot> = chartTileSnapshot()
        ) => {
          slowTilesFailed = hasFailedVisibleChartTiles(snapshot.outcomes, snapshot.visible);
          slowTilesUnsettled = hasUnsettledVisibleChartTiles(snapshot.outcomes, snapshot.visible);
          slowTilesInkless = hasInklessVisibleChartTiles(snapshot.outcomes, snapshot.visible);
        };
        const refreshNoticesAndSlowTiles = () => recomputeSlowTiles(chartCoverage());
        refreshChartNotices = refreshNoticesAndSlowTiles;

        /* A brief empty rectangle during a zoom is normal. It becomes useful
         * to mention only after three seconds, and `idle` proves every
         * currently requested square has either drawn or failed. */
        let slowTimer: ReturnType<typeof setTimeout> | null = null;
        let tilesWereSettled = true;
        const tilesLoading = () => {
          if (slowTimer === null) {
            if (tilesWereSettled) {
              slowTilesFailed = false;
              slowTilesUnsettled = false;
              slowTilesInkless = false;
              slowTilesGraceElapsed = false;
              slowTilesDismissed = false;
            }
            tilesWereSettled = false;
            recomputeSlowTiles();
            slowTimer = setTimeout(() => {
              slowTimer = null;
              if (!m.areTilesLoaded()) {
                slowTilesGraceElapsed = true;
                refreshNoticesAndSlowTiles();
              }
            }, 3000);
          } else {
            recomputeSlowTiles();
          }
        };
        const tilesSettled = () => {
          if (slowTimer !== null) clearTimeout(slowTimer);
          slowTimer = null;
          tilesWereSettled = true;
          refreshNoticesAndSlowTiles();
        };
        const offTileStatus = tileProtocol.onChartTileStatus(refreshNoticesAndSlowTiles);
        m.on('moveend', refreshNoticesAndSlowTiles);
        m.on('dataloading', tilesLoading);
        m.on('idle', tilesSettled);
        refreshNoticesAndSlowTiles();
        if (!m.areTilesLoaded()) tilesLoading();

        /* ═══ THE SWEEP ═══
         *
         * One timer drives every live layer. `setView` and `pollAvailability`
         * are called from HERE for the reason above — from a map event they run
         * inside an effect and Svelte throws, which kills the sweep and with it
         * every layer's update, silently. */
        ctx = { map: m, beforeIdFor: () => undefined };
        const timer = setInterval(() => {
          now = viewTime.now;
          for (const mod of layers.list()) {
            if (!isUpdatable(mod) || !drawn(mod.id)) continue;
            try {
              mod.update(ctx!);
            } catch (e) {
              /* One bad layer must not stop the other twenty. */
              console.warn(`[chart] layer "${mod.id}" failed to update:`, e);
            }
          }
          layers.setView(bounds());
          layers.pollAvailability();
          try {
            tick?.(env);
          } catch (e) {
            console.warn('[chart] the host’s tick failed:', e);
          }
        }, 1000);
        stop = () => {
          clearInterval(timer);
          if (slowTimer !== null) clearTimeout(slowTimer);
          offTileStatus();
          refreshChartNotices = null;
          m.off('moveend', refreshNoticesAndSlowTiles);
          m.off('dataloading', tilesLoading);
          m.off('idle', tilesSettled);
        };
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
      map?.remove();
      map = null;
      mapRef = null;
      ctx = null;
      mounted--;
    };
  });

  /* ═══ AND A LAYER SWITCHED BACK ON REDRAWS AT ONCE ═══
   *
   * Skipping invisible layers means a source can be up to a second stale when
   * its row is ticked. `layers.rev` and `layers.state` both change on a
   * visibility toggle, so this catches exactly that moment — without it,
   * turning a layer on shows the previous second's tide for a beat, which reads
   * as the toggle being slow. */
  $effect(() => {
    void layers.rev;
    void layers.state;
    if (ready) {
      untrack(() => {
        refresh();
        refreshChartNotices?.();
      });
    }
  });

  /* Theme changes after mount. The style itself is rebuilt by the host if it
   * swaps base maps; this is the layers' own day/night, which every module
   * applies through `applyTheme`. */
  $effect(() => {
    if (ready) layers.setNight(night);
  });

  /* ═══ THE PERSPECTIVE VIEW ═══
   *
   * Switching it OFF puts the chart back exactly as it was — flat, north-up,
   * untiltable — which is what makes 3D a contained addition rather than a
   * change to the working chart. Levelled BEFORE the cap is dropped, or the
   * chart stays stuck at whatever angle it was abandoned at with no gesture
   * left that can straighten it.
   *
   * Lifted from the boat's ChartPage, where the reasoning was worked out. */
  $effect(() => {
    const m = mapRef;
    const on = threeD;
    const ex = exaggeration;
    const n = night;
    if (!m || !ready) return;

    void (async () => {
      const { terrainSource, TERRAIN_SOURCE_ID } = await import(
        '@gennetay/chart-core/map/terrain-protocol'
      );
      const { skyFor } = await import('@gennetay/chart-core/lib/three-d.svelte');
      if (!on) {
        m.setTerrain(null);
        // There is no removeSky; zero atmosphere is how you put it away.
        m.setSky({ 'atmosphere-blend': 0 });
        if (m.getPitch() !== 0) m.easeTo({ pitch: 0, duration: 250 });
        m.setMaxPitch(0);
        m.dragRotate.disable();
        return;
      }
      if (!m.getSource(TERRAIN_SOURCE_ID)) m.addSource(TERRAIN_SOURCE_ID, terrainSource);
      m.setMaxPitch(85);
      m.dragRotate.enable(); // a mouse has no third finger
      m.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: ex });
      m.setSky(skyFor(n));
      /* Give it a tilt on the way in. Switching to 3D and seeing an identical
       * flat chart reads as the button being broken. */
      if (m.getPitch() < 5) m.easeTo({ pitch: 62, duration: 600 });
    })();
  });
</script>

<div class="map" bind:this={container}></div>

{#if error}
  <div class="fault">the chart failed to start — {error}</div>
{:else if !ready}
  <div class="fault quiet">Loading charts…</div>
{/if}

<div class="chart-notes">
  {#if loadingTimedOut}
    <ChartLoadingNotice
      requested={loadingRequested}
      affected={affectedLayerNames()}
      onretry={retryLoadingCharts}
    />
  {/if}

  {#if canadianGap && !canadianGapDismissed}
    <div class="chart-note" role="status">
      <span>Canadian charts don't cover these waters — showing the US chart.</span>
      <button aria-label="dismiss Canadian chart notice" onclick={() => (canadianGapDismissed = true)}>×</button>
    </div>
  {/if}

  {#each chartOutages.filter((outage) => !loadingTimedOut && !chartOutagesDismissed[outage.id]) as outage (outage.id)}
    <div class="chart-note" role="status">
      <span>{outage.name} aren't responding — the chart service is having trouble.</span>
      <button
        aria-label={`dismiss ${outage.name} outage notice`}
        onclick={() => (chartOutagesDismissed = { ...chartOutagesDismissed, [outage.id]: true })}>×</button
      >
    </div>
  {/each}

  {#if noOfficialCharts && !noOfficialChartsDismissed}
    <div class="chart-note" role="status">
      <span>No official charts cover these waters — the base map isn't for navigation.</span>
      <button
        aria-label="dismiss no official charts notice"
        onclick={() => (noOfficialChartsDismissed = true)}>×</button
      >
    </div>
  {/if}

  {#if slowTilesNoticeVisible({
    failed: slowTilesFailed,
    unsettled: slowTilesUnsettled,
    inkless: slowTilesInkless,
    graceElapsed: slowTilesGraceElapsed,
    dismissed: slowTilesDismissed,
    outage:
      (canadianGap && !canadianGapDismissed)
      || (noOfficialCharts && !noOfficialChartsDismissed)
      || chartOutages.some((outage) => !loadingTimedOut && !chartOutagesDismissed[outage.id]),
    timedOut: loadingTimedOut
  })}
    <SlowTilesNotice onretry={retryLoadingCharts} ondismiss={() => (slowTilesDismissed = true)} />
  {/if}
</div>

<style>
  /* Absolute, so the chart fills whatever the host gives it. A host that
   * forgets to size its container gets a zero-height chart, which is visible
   * immediately — a chart that sized itself would instead be subtly wrong. */
  .map {
    position: absolute;
    inset: 0;
  }
  .fault {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 10px 14px;
    border-radius: 6px;
    /* Self-contained, like every component in this package: a token if the host
     * has one, a sane value if it has not. */
    background: var(--panel);
    color: var(--ink);
    font: 12px/1.4 var(--mono);
    border: 1px solid var(--line);
  }
  .quiet {
    opacity: 0.7;
  }
  .chart-notes {
    position: absolute;
    z-index: 4;
    top: var(--sp-4);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    width: max-content;
    max-width: calc(100% - 2 * var(--sp-5));
    pointer-events: none;
  }
  .chart-note {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-5);
    border: 1px solid var(--am-line);
    border-radius: var(--r-2);
    background: var(--panel);
    color: var(--ink);
    font: var(--fs-read) / var(--lh-read) var(--sans);
    box-shadow: var(--el-2);
    pointer-events: auto;
  }
  .chart-note button {
    flex: none;
    width: var(--h-touch);
    min-height: var(--h-touch);
    border: 0;
    border-radius: var(--r-2);
    background: none;
    color: var(--sub);
    font: 20px/1 var(--sans);
    cursor: pointer;
  }
</style>
